// src/components/ShipmentsTable.js

import { useState, useMemo } from 'react';
import { Search, ChevronLeft, ChevronRight, ChevronDown, Filter } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

function FilterDropdown({ label, value, options, onChange, colors }) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: 'none',
          padding: '0.5rem 2rem 0.5rem 0.75rem',
          background: colors.bgElevated,
          border: 'none',
          borderRadius: '8px',
          color: colors.textPrimary,
          fontSize: '0.75rem',
          cursor: 'pointer',
          minWidth: '100px',
          transition: 'all 0.15s ease',
        }}
      >
        <option value="">{label}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
      <ChevronDown 
        size={12} 
        color={colors.textMuted}
        style={{
          position: 'absolute',
          right: '0.6rem',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function ShipmentsTable({ shipments, onSelectShipment }) {
  const { colors } = useTheme();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [carrierFilter, setCarrierFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 12;

  // Get unique filter options
  const filterOptions = useMemo(() => ({
    statuses: [...new Set(shipments?.map(s => s.status).filter(Boolean))],
    products: [...new Set(shipments?.map(s => s.productType).filter(Boolean))],
    carriers: [...new Set(shipments?.map(s => s.carrierName).filter(Boolean))],
    severities: ['Critical', 'Major', 'Minor'],
  }), [shipments]);

  // Filter shipments
  const filtered = useMemo(() => {
    if (!shipments || !Array.isArray(shipments)) return [];
    
    return shipments.filter(s => {
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch = search === '' || 
        s.id?.toLowerCase().includes(searchLower) ||
        s.productName?.toLowerCase().includes(searchLower) ||
        s.carrierName?.toLowerCase().includes(searchLower) ||
        s.originState?.toLowerCase().includes(searchLower) ||
        s.destinationState?.toLowerCase().includes(searchLower);
      
      // Dropdown filters
      const matchesStatus = statusFilter === '' || s.status === statusFilter;
      const matchesProduct = productFilter === '' || s.productType === productFilter;
      const matchesCarrier = carrierFilter === '' || s.carrierName === carrierFilter;
      const matchesSeverity = severityFilter === '' || s.excursionSeverity === severityFilter;
      
      return matchesSearch && matchesStatus && matchesProduct && matchesCarrier && matchesSeverity;
    });
  }, [shipments, search, statusFilter, productFilter, carrierFilter, severityFilter]);

  // Reset page when filters change
  useMemo(() => {
    setPage(1);
  }, [search, statusFilter, productFilter, carrierFilter, severityFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const current = filtered.slice((page - 1) * perPage, page * perPage);

  const getStatusStyle = (status) => {
    const styles = {
      'Delivered': { color: colors.success, bg: colors.successMuted },
      'In Transit': { color: colors.info, bg: colors.infoMuted },
      'Pending': { color: colors.warning, bg: colors.warningMuted },
      'Delayed': { color: colors.warning, bg: colors.warningMuted },
      'Quarantined': { color: colors.danger, bg: colors.dangerMuted },
    };
    return styles[status] || { color: colors.textMuted, bg: colors.bgElevated };
  };

  const getSeverityStyle = (sev) => {
    const styles = {
      'Critical': { color: colors.danger, bg: colors.dangerMuted },
      'Major': { color: colors.warning, bg: colors.warningMuted },
      'Minor': { color: colors.info, bg: colors.infoMuted },
    };
    return styles[sev] || null;
  };

  // Clear all filters
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setProductFilter('');
    setCarrierFilter('');
    setSeverityFilter('');
  };

  const hasActiveFilters = search || statusFilter || productFilter || carrierFilter || severityFilter;

  if (!shipments || shipments.length === 0) {
    return (
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        padding: '3rem',
        textAlign: 'center',
        color: colors.textMuted,
        boxShadow: colors.cardShadow,
      }}>
        No shipments available
      </div>
    );
  }

  return (
    <div style={{
      background: colors.bgCard,
      backgroundImage: colors.gradientCard,
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: colors.cardShadow,
    }}>
      {/* Header with Search and Filters */}
      <div style={{
        padding: '1rem 1.25rem',
        background: colors.bgSecondary,
      }}>
        {/* Search Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '0.75rem',
        }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={15} style={{ 
              position: 'absolute', 
              left: '0.85rem', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: colors.textMuted 
            }} />
            <input
              type="text"
              placeholder="Search shipments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '0.6rem 1rem 0.6rem 2.5rem',
                background: colors.bgElevated,
                border: 'none',
                borderRadius: '10px',
                color: colors.textPrimary,
                fontSize: '0.8rem',
              }}
            />
          </div>
          <span style={{ fontSize: '0.7rem', color: colors.textMuted, whiteSpace: 'nowrap' }}>
            {filtered.length} of {shipments.length}
          </span>
        </div>

        {/* Filters Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}>
          <Filter size={14} color={colors.textMuted} />
          <FilterDropdown 
            label="All Status" 
            value={statusFilter} 
            options={filterOptions.statuses} 
            onChange={setStatusFilter} 
            colors={colors} 
          />
          <FilterDropdown 
            label="All Products" 
            value={productFilter} 
            options={filterOptions.products} 
            onChange={setProductFilter} 
            colors={colors} 
          />
          <FilterDropdown 
            label="All Carriers" 
            value={carrierFilter} 
            options={filterOptions.carriers} 
            onChange={setCarrierFilter} 
            colors={colors} 
          />
          <FilterDropdown 
            label="Severity" 
            value={severityFilter} 
            options={filterOptions.severities} 
            onChange={setSeverityFilter} 
            colors={colors} 
          />
          
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                padding: '0.4rem 0.75rem',
                background: colors.dangerMuted,
                border: 'none',
                borderRadius: '6px',
                color: colors.danger,
                fontSize: '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
                marginLeft: 'auto',
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Shipment ID', 'Product', 'Route', 'Carrier', 'Status', 'Severity'].map(h => (
                <th key={h} style={{
                  padding: '0.75rem 1rem',
                  textAlign: 'left',
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  background: colors.bgSecondary,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {current.length > 0 ? current.map((s, i) => {
              const statusStyle = getStatusStyle(s.status);
              const sevStyle = getSeverityStyle(s.excursionSeverity);
              
              return (
                <tr
                  key={s.id || i}
                  onClick={() => onSelectShipment && onSelectShipment(s)}
                  style={{
                    cursor: 'pointer',
                    background: i % 2 ? colors.tableRowAlt : 'transparent',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = colors.tableRowHover}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 ? colors.tableRowAlt : 'transparent'}
                >
                  <td style={{ padding: '0.85rem 1rem' }}>
                    <span style={{
                      fontSize: '0.8rem',
                      color: colors.primary,
                      fontWeight: 600,
                      fontFeatureSettings: '"tnum"',
                    }}>
                      {s.id || 'N/A'}
                    </span>
                  </td>
                  <td style={{ padding: '0.85rem 1rem' }}>
                    <div style={{ fontSize: '0.8rem', color: colors.textPrimary, fontWeight: 500 }}>
                      {s.productName?.split(' ')[0] || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '0.65rem', color: colors.textMuted }}>
                      {s.productType || ''}
                    </div>
                  </td>
                  <td style={{ padding: '0.85rem 1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                      {s.originState || 'N/A'} → {s.destinationState || 'N/A'}
                    </div>
                  </td>
                  <td style={{ padding: '0.85rem 1rem', fontSize: '0.75rem', color: colors.textSecondary }}>
                    {s.carrierName?.split(' ')[0] || 'N/A'}
                  </td>
                  <td style={{ padding: '0.85rem 1rem' }}>
                    <span style={{
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      color: statusStyle.color,
                      background: statusStyle.bg,
                      padding: '0.25rem 0.6rem',
                      borderRadius: '6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.3rem',
                    }}>
                      <span style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        background: statusStyle.color,
                      }} />
                      {s.status || 'Unknown'}
                    </span>
                  </td>
                  <td style={{ padding: '0.85rem 1rem' }}>
                    {sevStyle ? (
                      <span style={{
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        color: sevStyle.color,
                        background: sevStyle.bg,
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                      }}>
                        {s.excursionSeverity}
                      </span>
                    ) : (
                      <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>—</span>
                    )}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: colors.textMuted }}>
                  No shipments match your filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{
        padding: '0.85rem 1.25rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: colors.bgSecondary,
      }}>
        <span style={{ fontSize: '0.7rem', color: colors.textMuted }}>
          Showing {((page - 1) * perPage) + 1}-{Math.min(page * perPage, filtered.length)} of {filtered.length}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            style={{
              padding: '0.4rem 0.6rem',
              background: colors.bgElevated,
              border: 'none',
              borderRadius: '8px',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              opacity: page === 1 ? 0.4 : 1,
              color: colors.textSecondary,
              display: 'flex',
              transition: 'all 0.15s',
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: '0.75rem', color: colors.textSecondary, fontWeight: 500 }}>
            {page} / {totalPages || 1}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            style={{
              padding: '0.4rem 0.6rem',
              background: colors.bgElevated,
              border: 'none',
              borderRadius: '8px',
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              opacity: page >= totalPages ? 0.4 : 1,
              color: colors.textSecondary,
              display: 'flex',
              transition: 'all 0.15s',
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ShipmentsTable;